const ICON_TOOLTIP_SELECTOR = ".management_room_icon_item[data-tooltip]";
const PERMISSION_TOOLTIP_SELECTOR = [
  ".management_action_button.is_permission_blocked[data-disabled-tooltip]",
  ".management_options_item.is_permission_blocked[data-disabled-tooltip]",
  ".management_order_arrow.is_permission_blocked[data-disabled-tooltip]",
  ".management_order_action_field.is_permission_blocked[data-disabled-tooltip]",
  ".management_catalog_button.is_permission_blocked[data-disabled-tooltip]"
].join(", ");

let activeCleanup = null;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function normalizeElement(node) {
  if (!node) return null;
  if (node.nodeType === Node.ELEMENT_NODE) return node;
  return node.parentElement || null;
}

function findTooltipTarget(node) {
  const element = normalizeElement(node);
  if (!element || !element.closest) return null;

  const permissionTarget = element.closest(PERMISSION_TOOLTIP_SELECTOR);
  if (permissionTarget) {
    const text = permissionTarget.getAttribute("data-disabled-tooltip");
    if (text) return { element: permissionTarget, text, variant: "danger" };
  }

  const iconTarget = element.closest(ICON_TOOLTIP_SELECTOR);
  if (iconTarget) {
    const text = iconTarget.getAttribute("data-tooltip");
    if (text) return { element: iconTarget, text, variant: "dark" };
  }

  return null;
}

export function initManagementTooltipPortal() {
  if (typeof window === "undefined" || typeof document === "undefined") return () => {};
  if (activeCleanup) return activeCleanup;

  const tooltip = document.createElement("div");
  tooltip.className = "management_floating_tooltip";
  tooltip.setAttribute("role", "tooltip");

  const textNode = document.createElement("span");
  textNode.className = "management_floating_tooltip_text";
  tooltip.appendChild(textNode);
  document.body.appendChild(tooltip);

  let activeElement = null;
  let activeText = "";
  let activeVariant = "dark";

  function hideTooltip() {
    activeElement = null;
    activeText = "";
    tooltip.classList.remove("is_visible");
  }

  function positionTooltip() {
    if (!activeElement || !document.body.contains(activeElement)) {
      hideTooltip();
      return;
    }

    const rect = activeElement.getBoundingClientRect();
    if (!rect.width && !rect.height) {
      hideTooltip();
      return;
    }

    const viewportPadding = 8;
    const targetCenter = rect.left + rect.width / 2;
    const tooltipWidth = tooltip.offsetWidth || 0;
    const tooltipHeight = tooltip.offsetHeight || 0;
    const minLeft = viewportPadding + tooltipWidth / 2;
    const maxLeft = window.innerWidth - viewportPadding - tooltipWidth / 2;
    const left = clamp(targetCenter, minLeft, Math.max(minLeft, maxLeft));
    const minTop = viewportPadding + tooltipHeight;
    const top = Math.max(rect.top - 10, minTop);
    const tooltipLeftEdge = left - tooltipWidth / 2;
    const arrowLeft = clamp(targetCenter - tooltipLeftEdge, 14, Math.max(14, tooltipWidth - 14));

    tooltip.style.setProperty("--management-tooltip-left", `${Math.round(left)}px`);
    tooltip.style.setProperty("--management-tooltip-top", `${Math.round(top)}px`);
    tooltip.style.setProperty("--management-tooltip-arrow-left", `${Math.round(arrowLeft)}px`);
  }

  function showTooltip(target) {
    if (!target) {
      hideTooltip();
      return;
    }

    const changed = activeElement !== target.element || activeText !== target.text || activeVariant !== target.variant;
    activeElement = target.element;
    activeText = target.text;
    activeVariant = target.variant;

    if (changed) {
      textNode.textContent = target.text;
      tooltip.setAttribute("data-variant", target.variant);
    }

    tooltip.classList.add("is_visible");
    positionTooltip();
  }

  function handlePointerMove(event) {
    const elementAtPointer = document.elementFromPoint(event.clientX, event.clientY);
    showTooltip(findTooltipTarget(elementAtPointer));
  }

  function handleFocusIn(event) {
    showTooltip(findTooltipTarget(event.target));
  }

  function handleFocusOut() {
    hideTooltip();
  }

  function handleScrollOrResize() {
    if (activeElement) positionTooltip();
  }

  document.addEventListener("pointermove", handlePointerMove, true);
  document.addEventListener("pointerleave", hideTooltip, true);
  document.addEventListener("focusin", handleFocusIn, true);
  document.addEventListener("focusout", handleFocusOut, true);
  window.addEventListener("scroll", handleScrollOrResize, true);
  window.addEventListener("resize", handleScrollOrResize);

  activeCleanup = function cleanupManagementTooltipPortal() {
    document.removeEventListener("pointermove", handlePointerMove, true);
    document.removeEventListener("pointerleave", hideTooltip, true);
    document.removeEventListener("focusin", handleFocusIn, true);
    document.removeEventListener("focusout", handleFocusOut, true);
    window.removeEventListener("scroll", handleScrollOrResize, true);
    window.removeEventListener("resize", handleScrollOrResize);
    tooltip.remove();
    activeCleanup = null;
  };

  return activeCleanup;
}
